import { Autocomplete, Grid, TextField } from '@mui/material';
import {
  getAvailableBusinessForFund,
  getSharedBusinessDataHubLightReport,
} from 'lib/api/multiverse/fund';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { TemporaryBusiness } from '../../types/businessRelations';

const TemporaryBusinesses: React.FC = () => {
  const dispatch = useDispatch();
  const [temporaryRelations, setTemporaryRelations] = useState<
    TemporaryBusiness[]
  >([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState<
    TemporaryBusiness[]
  >([]);
  const [loading, setLoading] = useState(false);

  const setUp = async () => {
    setLoading(true);
    const temporaryRelationsResponse =
      await getAvailableBusinessForFund(dispatch);
    setTemporaryRelations(temporaryRelationsResponse || []);
    setFilteredBusinesses(temporaryRelationsResponse || []);
    setLoading(false);
  };

  useEffect(() => {
    void setUp();
  }, []);

  const exportLightReport = async (id: number) => {
    try {
      setLoading(true);
      const business = temporaryRelations.find(
        (x: TemporaryBusiness) => x.id === id,
      );
      if (!business) return;

      const { fileUrl, errors } = await getSharedBusinessDataHubLightReport(
        dispatch,
        business.businessIdentifier,
        business.companyId,
      );
      if (errors.length > 0) {
        alert(
          `Ha ocurrido un error: ${errors.map((x: string) => x).join(', \n')}`,
        );
      }
      window.open(fileUrl, '_blank');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
    setLoading(false);
  };

  const canExportLightReport = (id: number) => {
    const business = temporaryRelations.find(
      (x: TemporaryBusiness) => x.id === id,
    );
    if (!business) return false;

    if (business.active) return true;
    return false;
  };

  const actions = [
    {
      action: exportLightReport,
      actionLabel: 'Exportar Reporte',
      canShow: canExportLightReport,
    },
  ];

  const handleChange = (event: unknown, newValue: TemporaryBusiness | null) => {
    if (!newValue) return setFilteredBusinesses(temporaryRelations);
    const filtered = temporaryRelations.filter(
      (x: TemporaryBusiness) => x.id === newValue.id,
    );
    setFilteredBusinesses(filtered);
  };

  const AutoCompleteFilter = () => (
    <Autocomplete
      options={temporaryRelations}
      getOptionLabel={(option: TemporaryBusiness) =>
        `${option.businessName} - ${option.businessIdentifier}`
      }
      renderInput={(params) => (
        <TextField style={{ minWidth: 250 }} {...params} label="Filtrar" />
      )}
      onChange={handleChange}
      fullWidth
    />
  );

  if (loading) return <CenteredCircularProgress />;

  return (
    <div>
      <HeaderWithActions
        type="subHeader"
        title="Estas son las cuentas que te han compartido. Tus clientes pueden desactivarlas cuando lo requieran."
        variant="body1"
        item={
          <Grid container>
            <Grid item xs={12}>
              <AutoCompleteFilter />
            </Grid>
          </Grid>
        }
      />
      <EnhancedTable
        dataHeaders={[
          { key: 'businessName', label: 'Cliente' },
          { key: 'businessIdentifier', label: 'Rut' },
          { key: 'active', label: 'SII', type: 'sii-connection-temporary' },
          { key: 'companyName', label: 'Compartido por' },
          { key: 'expiresAt', label: 'Compartido hasta', type: 'date' },
        ]}
        data={filteredBusinesses}
        rowsPerPageDefault={25}
        showActions
        actions={actions}
      />
    </div>
  );
};

export default TemporaryBusinesses;
